import React, { useState, useEffect, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Card from '@amzn/meridian/card';
import Box from '@amzn/meridian/box';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import plusKnockoutTokens from '@amzn/meridian-tokens/base/icon/plus-knockout';
import minusKnockoutTokens from '@amzn/meridian-tokens/base/icon/minus-knockout';
import closeKnockoutTokens from '@amzn/meridian-tokens/base/icon/close-knockout';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { AgileRecommendationTypes } from '@components/agile/types/AgileRecommendationTypes';
import useGetSwapRecommendationsResults from '@store/hooks/useGetSwapRecommendationsResults';
import useSwapAssortment from '@store/hooks/useSwapAssortment';
import useAllZonesData from 'src/hooks/useAllZonesData';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import AssortmentFacingUpdates from '@components/agile/types/AssortmentFacingUpdates';
import { ERROR_COLOR, SUCCESS_COLOR, BLUE_COLOR } from 'src/constants/values';
import AgileURLParameterTypes from '@components/agile/types/AgileURLParameterTypes';
import { useNavigate } from 'react-router-dom';
import { CellValueChangedEvent } from 'ag-grid-community';
import getDateInUTC from '@components/agile/utils/GetDateUtils';
import { colDefs, calculateSum } from '@components/agile/utils/RecommendationTableUtils';
import Column from '@amzn/meridian/column';
import { StackedCapacityBar } from './StackedCapacityBar';
import { useZoneCapacityCalculator } from 'src/hooks/useZoneCapacityData';
import { useZoneCapacityByAsinSegments } from 'src/hooks/useZoneCapacityByAsinSegments';
import { useAllZoneCapacityByZones } from 'src/hooks/useAllZoneCapacityByZones';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import FeatureName from '@constants/featureName';
import ColumnSelector from '@components/shared/ColumnSelector';
import { useColumnSelector } from 'src/hooks/useColumnSelector';

export interface AtRecommendationTableProps {
  zoneDetails: AgileMacroplanogram | undefined;
  swapTime: number;
  isDraft: boolean;
  allZones?: AgileMacroplanogram[];
}

const AtModifyRecommendationTable = (props: AtRecommendationTableProps) => {
  const navigate = useNavigate();
  const [rowData, setRowData] = useState<AgileRecommendationTypes[]>([]);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [successful, setSuccessful] = useState(false);
  const [swapErrorMsg, setSwapErrorMsg] = useState<string | undefined>(undefined);
  const [disableSwapButton, setDisableSwapButton] = useState<boolean>(true);
  const [recommenationFalg, setRecommendationFlag] = useState<boolean>(true);
  const [enableLoader, setLoader] = useState(true);
  const [modifyTotalFacings, setModifyTotalFacings] = useState(0);
  const [facingListToUpdate, setFacingListToUpdate] = useState<AgileRecommendationTypes[] | undefined>(undefined);
  const [unitsToRemove, setUnitsToRemove] = useState<number>(-1);
  const [facingUnitsToRemoveList, setfacingUnitsToRemove] = useState<AssortmentFacingUpdates[]>([]);
  const { zoneDetails, swapTime, isDraft, allZones = [] } = props;
  const { mutateAsync: swapAssortment } = useSwapAssortment();
  const isV2Enabled = useFeatureFlag(FeatureName.NEXT_GEN_V2);

  const defaultColDef = useMemo(
    () => ({
      editable: false,
      sortable: true,
      resizable: true,
      filter: true,
      flex: 1,
      minWidth: 100,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    }),
    [],
  );

  // Column selection for modify/reduce table
  const defaultVisibleColumns = [
    'asin',
    'title',
    'image',
    'remainingFacings',
    'difference',
    'remainingCapacity',
    'uphl1',
    'uphl2',
    'uphl3',
    'shrinkPerOps',
    'shrinkPerOps13week',
    'shrinkPerOps26week',
    'movingAvgSales13week',
    'movingAvgSales52week',
  ];

  const { visibleColumns, columnDefs, toggleColumn } = useColumnSelector({
    allColumns: colDefs,
    defaultVisibleColumns,
    storageKey: 'modifyRecommendationTableColumns',
  });

  const response = useGetSwapRecommendationsResults({
    storeId: zoneDetails?.storeId ?? '',
    macroplanogramId: zoneDetails?.entryId ?? '',
    swapTime,
    endTime: zoneDetails?.endTime,
    addAssortment: [],
    includeDraft: isDraft,
  });

  // Get real ASIN data from all zones for capacity calculations
  const allZonesRowData = useAllZonesData(isV2Enabled, zoneDetails?.storeId ?? '', allZones || [], swapTime, isDraft);

  // Get zoneMapConfigId from zones that have it, fallback to undefined for legacy zones
  const zoneWithConfig = isV2Enabled ? allZones.find((zone) => zone.zoneMapConfigId) : undefined;
  const zoneMapConfigId = zoneWithConfig?.zoneMapConfigId || undefined;

  const { enhancedData: zoneFixtureDataRaw, allZonesTotalFixtureCapacity } = useZoneCapacityCalculator(
    isV2Enabled ? (zoneDetails?.storeId ?? '') : '',
    isV2Enabled ? allZones : [],
    isV2Enabled ? allZonesRowData : [],
    'Produce',
    zoneMapConfigId,
  );

  // Use zone segments - conditionally process data based on feature flag
  const allZonesSegments = useAllZoneCapacityByZones(isV2Enabled ? zoneFixtureDataRaw : [], isV2Enabled ? rowData : []);

  const currentZoneAsinSegments = useZoneCapacityByAsinSegments(isV2Enabled ? rowData : []);

  useEffect(() => {
    const tempRowData: AgileRecommendationTypes[] = [];
    if (response.results !== undefined) {
      setLoader(false);
    }
    const recommendationsResult = response.results ? response.results : [];
    setDisableSwapButton(true);
    setRecommendationFlag(true);
    setModifyTotalFacings(response.occupiedFacingUnits);
    recommendationsResult.forEach((rawRow) => {
      const rowObj = {
        asin: rawRow.asin,
        availableFacings: rawRow.availableFacings,
        facingToRemove: rawRow.facingToRemove,
        entryId: rawRow.entryId,
        fohCapacity: rawRow.fohCapacity,
        uphl1: rawRow.uphl1,
        uphl2: rawRow.uphl2,
        uphl3: rawRow.uphl3,
        title: rawRow.title,
        image: rawRow.image,
        remainingFacings: rawRow.remainingFacings,
        remainingCapacity: rawRow.remainingCapacity,
        difference: rawRow.difference,
        zoneId: zoneDetails?.entryId,
        shrinkCost: rawRow.shrinkCost ?? 'N/A',
        shrinkPerOps: rawRow.shrinkPerOps ?? 'N/A',
        shrinkPerOps13week: rawRow.shrinkPerOps13week ?? 'N/A',
        shrinkPerOps26week: rawRow.shrinkPerOps26week ?? 'N/A',
        shrinkPerOps52week: rawRow.shrinkPerOps52week ?? 'N/A',
        movingAvgSales13week: rawRow.movingAvgSales13week ?? 'N/A',
        movingAvgSales26week: rawRow.movingAvgSales26week ?? 'N/A',
        movingAvgSales52week: rawRow.movingAvgSales52week ?? 'N/A',
      } as AgileRecommendationTypes;
      tempRowData.push(rowObj);
    });
    setRowData(tempRowData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response.results]);

  const onSwapAssortment = async () => {
    await swapAssortment({
      input: {
        storeId: zoneDetails?.storeId ? zoneDetails.storeId : '',
        macroplanogramId: zoneDetails?.entryId ? zoneDetails.entryId : '',
        swapTime,
        endTime: zoneDetails?.endTime,
        addAssortment: [],
        assortmentFacingUpdates: facingUnitsToRemoveList,
        isDraft,
      },
    }).then((resp) => {
      // eslint-disable-next-line promise/always-return
      if (resp.swapAssortment.error === null) {
        setOpenConfirmationModal(false);
        setSuccessful(true);
      } else {
        setSwapErrorMsg(resp.swapAssortment.error?.errorMessage);
      }
    });
  };

  const onClick = () => {
    const filterFacingUpdatesList = rowData?.filter((data) => +data.difference !== +0);
    setFacingListToUpdate(filterFacingUpdatesList);
    const facingUpdatesList = filterFacingUpdatesList?.map<AssortmentFacingUpdates>((data) => ({
      entryId: data.entryId,
      facingUnits: parseInt(String(data.remainingFacings), 10),
      fohCapacity: data.remainingCapacity,
    }));
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setfacingUnitsToRemove(facingUpdatesList);
    if (!disableSwapButton) {
      setOpenConfirmationModal(true);
    } else {
      setOpenConfirmationModal(false);
    }
  };

  const onClose = () => {
    const date = getDateInUTC(swapTime);
    setOpenConfirmationModal(false);
    setSuccessful(false);
    navigate('/assortmentSearch', {
      state: {
        storeId: zoneDetails?.storeId,
        date,
        zoneId: zoneDetails?.entryId,
      } as AgileURLParameterTypes,
    });
  };
  const onCancelSwapAssortment = () => {
    setOpenConfirmationModal(false);
    setSuccessful(false);
  };

  /* On changing facing units to remove, onCellValueChanged function will
       update row data and will enable swap button if unitsToRemove
        is equal to total input facing units.
     */
  const onCellValueChangedHandler = (event: CellValueChangedEvent<AgileRecommendationTypes>) => {
    if (event.rowIndex != null) {
      const newData = [...rowData];
      const rowIndex = newData.findIndex((item) => item.asin === event.data.asin);
      newData[rowIndex].difference = event.data.remainingFacings - event.data.availableFacings;
      newData[rowIndex].remainingCapacity = Math.round(
        (event.data.fohCapacity / event.data.availableFacings) * event.data.remainingFacings,
      );
      newData[rowIndex] = event.data;
      setRowData(newData);
      const removedFacingsSum = calculateSum(rowData, 'difference');
      const totalNewFacing = response.occupiedFacingUnits + removedFacingsSum;
      const facingsToRemove = totalNewFacing - response.maxFacingUnits;
      setUnitsToRemove(facingsToRemove);
      setModifyTotalFacings(totalNewFacing);
      const every = rowData.every((obj) => obj.difference === 0);
      if (every) setDisableSwapButton(true);
      // eslint-disable-next-line eqeqeq
      else if (totalNewFacing >= response.occupiedFacingUnits && totalNewFacing <= response.maxFacingUnits) {
        setDisableSwapButton(false);
      } else {
        setDisableSwapButton(true);
      }
    }
  };

  return enableLoader ? (
    <Column alignmentHorizontal="center" alignmentVertical="center" spacing="800" spacingInset="500">
      <Loader type="circular" size="large" />{' '}
    </Column>
  ) : (
    <>
      {isDraft && (
        <Row spacing="400" alignmentHorizontal="center" spacingInset="400">
          <Alert size="large" type="informational">
            You are working in a draft zone - all assortment additions and modifications will be saved as drafts for
            later review
          </Alert>
        </Row>
      )}
      <Row spacing="400" alignmentHorizontal="justify" spacingInset="400">
        <Text type="h300">
          {`Adjust existing capacity of products in the ${zoneDetails?.macroplanogramName} zone.`}
          <Text type="b100">Reduce/remove facings of existing products until the zone is completely full</Text>
        </Text>
        <Box type="outline" spacingInset="400">
          <Text type="h200">{`Existing Facings: ${response.occupiedFacingUnits}/${response.maxFacingUnits}`}</Text>
          <Text type="h200">{`New Total Facings: ${modifyTotalFacings}/${response.maxFacingUnits}`}</Text>
          {modifyTotalFacings < response.maxFacingUnits && (
            <Alert type="warning" size="small">
              <Text type="h200">Zone is not full</Text>
            </Alert>
          )}
          {unitsToRemove > 0 && (
            <Alert type="error" size="small">
              <Text type="h200">{`Facings to remove: ${unitsToRemove}`}</Text>
            </Alert>
          )}
        </Box>
      </Row>
      {isV2Enabled && (
        <Row spacing="400" alignmentHorizontal="center" spacingInset="300">
          <Box width={'100%'}>
            <Box type="outline" spacingInset="300">
              <Box spacingInset={'300'}>
                <Text type="h500">Department Capacity Utilization</Text>
              </Box>
              {allZones.length > 0 && (
                <Box spacingInset={'300'}>
                  <Text type="h400">All Zones Overview</Text>
                  <StackedCapacityBar
                    total={allZonesTotalFixtureCapacity}
                    segments={allZonesSegments}
                    height={24}
                    showLegend={true}
                    showValues={true}
                    valueText="percent"
                    hideValues={true}
                    showOverflow={true}
                  />
                </Box>
              )}
              <Box spacingInset={'300'}>
                <Text type="h400">{zoneDetails?.macroplanogramName} Zone </Text>
                <StackedCapacityBar
                  total={response.maxFacingUnits || 0}
                  segments={currentZoneAsinSegments}
                  height={24}
                  showLegend={true}
                  showValues={true}
                  valueText="percent"
                  showOverflow={true}
                />
              </Box>
              <Row alignmentHorizontal="justify" spacingInset={'300'}>
                <Text type="b200">
                  Current: {modifyTotalFacings} / {response.maxFacingUnits} facings{' '}
                </Text>
                <Text type="b200">Remaining: {(response.maxFacingUnits || 0) - modifyTotalFacings} </Text>
              </Row>
            </Box>
          </Box>
        </Row>
      )}
      {recommenationFalg ? (
        <>
          <Row alignmentHorizontal="end" spacingInset="400">
            <ColumnSelector allColumns={colDefs} visibleColumns={visibleColumns} onToggleColumn={toggleColumn} />
          </Row>
          <Row spacing="400" alignmentHorizontal="center" spacingInset="400">
            <div style={{ height: 'auto', width: 2000 }} className="ag-theme-alpine">
              {rowData && (
                <AgGridReact<AgileRecommendationTypes>
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowSelection="multiple"
                  suppressRowClickSelection
                  enableCellTextSelection
                  domLayout="autoHeight"
                  onGridReady={(params) => {
                    params.api.sizeColumnsToFit();
                  }}
                  onCellValueChanged={onCellValueChangedHandler}
                />
              )}
              {(modifyTotalFacings < response.maxFacingUnits || unitsToRemove > 0) && (
                <Alert size="small" type="error">
                  <Text>Zone is not full or has more facings than allowed.</Text>
                </Alert>
              )}
            </div>
          </Row>
        </>
      ) : (
        <Text type="h300" alignment="center">
          Nothing to remove. Please Click submit button to add new asins.
        </Text>
      )}
      <Row alignmentHorizontal="center" spacingInset="600" spacing="400" backgroundColor="subdued">
        <Button onClick={onClick} disabled={disableSwapButton} data-testid="DisableSwapButton">
          Review changes
        </Button>
        <Modal open={openConfirmationModal} scrollContainer="viewport" width="500" aria-describedby="modal-description">
          <Alert size="large" type="warning">
            Please review and confirm the changes.
          </Alert>
          <Row alignmentHorizontal="center" spacingInset="300" spacing="400">
            <Card>
              <Table headerRows={1} spacing="small">
                <TableRow>
                  <TableCell alignmentHorizontal="center">ASIN</TableCell>
                  <TableCell alignmentHorizontal="center">Title</TableCell>
                  <TableCell alignmentHorizontal="center">Change</TableCell>
                  <TableCell alignmentHorizontal="center">Diff</TableCell>
                  <TableCell alignmentHorizontal="center">Live Date</TableCell>
                  <TableCell alignmentHorizontal="center">UPHL2</TableCell>
                  <TableCell alignmentHorizontal="center">#New Total Facings</TableCell>
                </TableRow>
                {facingListToUpdate &&
                  facingListToUpdate.map((data) => (
                    <TableRow>
                      <TableCell alignmentHorizontal="center">{data.asin}</TableCell>
                      <TableCell alignmentHorizontal="center">{data.title}</TableCell>
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {+data.remainingFacings === 0 ? (
                        <Text className={ERROR_COLOR}>
                          <TableCell alignmentHorizontal="center">
                            <Icon tokens={closeKnockoutTokens} />
                          </TableCell>
                        </Text>
                      ) : data.difference < 0 ? (
                        <Text className={BLUE_COLOR}>
                          <TableCell alignmentHorizontal="center">
                            <Icon tokens={minusKnockoutTokens} />
                          </TableCell>
                        </Text>
                      ) : (
                        <Text className={SUCCESS_COLOR}>
                          <TableCell alignmentHorizontal="center">
                            <Icon tokens={plusKnockoutTokens} />
                          </TableCell>
                        </Text>
                      )}
                      <TableCell alignmentHorizontal="center">{data.difference}</TableCell>
                      <TableCell alignmentHorizontal="center">
                        <Text>{getDateInUTC(swapTime)}</Text>
                      </TableCell>
                      <TableCell alignmentHorizontal="center">{data.uphl2}</TableCell>
                      <TableCell alignmentHorizontal="center">
                        <Text>{data.remainingFacings}</Text>
                      </TableCell>
                    </TableRow>
                  ))}
              </Table>
            </Card>
          </Row>
          <ModalFooter>
            <Row alignmentHorizontal="center" widths="fit">
              <Button type="secondary" size="large" onClick={onCancelSwapAssortment} data-testid="CancelSwapButton">
                Cancel
              </Button>
              <Button type="primary" size="large" onClick={onSwapAssortment} data-testid="SwapButton">
                Submit
              </Button>
            </Row>
          </ModalFooter>
        </Modal>
        {swapErrorMsg && (
          <Modal
            open={openConfirmationModal}
            scrollContainer="viewport"
            aria-describedby="modal-description"
            onClose={() => {
              setSwapErrorMsg(undefined);
              setOpenConfirmationModal(false);
            }}
          >
            <Alert size="xlarge" type="error">
              <h2>Error Message!</h2>
            </Alert>
            <Text>{swapErrorMsg}</Text>
            <Row alignmentHorizontal="center" widths="fit" />
          </Modal>
        )}
        {successful ? (
          <Modal open={successful} scrollContainer="viewport" aria-describedby="modal-description" onClose={onClose}>
            <Alert type="success" size="xlarge">
              ASINs Swapped Successfully
            </Alert>
          </Modal>
        ) : null}
      </Row>
    </>
  );
};
export default React.memo(AtModifyRecommendationTable);
